import { useMedplum } from "@medplum/react-hooks";
import {
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationUnread,
  type NotificationSubjectTask,
  useNotifications,
} from "@repo/data-store";
import { Button } from "@repo/ui/components/ui/button";
import { Label } from "@repo/ui/components/ui/label";
import { Popover, PopoverContent, PopoverHeader, PopoverTrigger } from "@repo/ui/components/ui/popover";
import { Switch } from "@repo/ui/components/ui/switch";
import { Link } from "@tanstack/react-router";
import { Bell, CheckCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { useNotificationActions, useNotificationOpen } from "@/app/providers/use-notification-store";
import { kindVerb, senderLabel, subjectPhrase } from "@/app/surfaces/inbox/inbox-copy";
import { practitionerFhirIdFromProfile } from "@/shared/hooks/use-current-practitioner";
import { formatRelativeTime } from "@/shared/lib/date-formats";

/** The "task MF-123 Description" tail, resolved from the subject Task at read-time.
 *  Bolds the MF number; falls back gracefully while the Task loads or if it's gone. */
function renderTaskPhrase(task: NotificationSubjectTask | undefined) {
  if (task?.number) {
    return (
      <>
        task <span className="font-semibold">{task.number}</span>
        {task.description ? ` ${task.description}` : ""}
      </>
    );
  }
  if (task?.description) return <>task {task.description}</>;
  return "a task";
}

/**
 * The peek surface: unread badge plus the most recent items, for triage without
 * leaving the current page. The full surface is `/inbox` — anything beyond
 * "glance and go" belongs there, not here.
 *
 * Display text is resolved here, never stored: the Communication is a pure
 * reference (see `@/app/surfaces/inbox/inbox-copy`, shared with the inbox).
 */
export function NotificationBell() {
  const medplum = useMedplum();
  const practitionerId = practitionerFhirIdFromProfile(medplum.getProfile());
  const { data: notifications } = useNotifications({ practitionerId });
  const open = useNotificationOpen();
  const { setOpen } = useNotificationActions();

  const [filter, setFilter] = useState<string>("all");

  const unreadCount = useMemo(() => notifications?.filter((comm) => comm.unread).length ?? 0, [notifications]);

  const filteredNotifications = useMemo(() => {
    if (!notifications) return undefined;
    if (filter === "unread") return notifications.filter((comm) => comm.unread);
    return notifications;
  }, [notifications, filter]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className="relative inline-flex size-8 items-center justify-center rounded-md hover:bg-accent cursor-pointer">
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="bg-primary text-destructive-foreground absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full px-1 text-xs font-medium">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 gap-0 overflow-clip p-0">
        <PopoverHeader className="flex-row items-center justify-between border-b px-3 py-2">
          <div className="flex items-center gap-2">
            <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Unread Only
              <Switch
                size="sm"
                checked={filter === "unread"}
                onCheckedChange={(checked) => setFilter(checked ? "unread" : "all")}
              />
            </Label>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className={`text-muted-foreground h-auto px-1.5 py-0.5 text-xs ${unreadCount > 0 ? "" : "invisible"}`}
            onClick={() => markAllNotificationsRead()}
          >
            <CheckCheck className="mr-1 h-3 w-3" />
            Mark all read
          </Button>
        </PopoverHeader>
        <div className="max-h-80 overflow-y-auto">
          {!filteredNotifications?.length ? (
            <p className="text-muted-foreground py-6 text-center text-sm">No notifications</p>
          ) : (
            filteredNotifications.map((comm) => {
              const isUnread = comm.unread;
              // Opening from the bell means leaving the page, so land on the
              // inbox with this item selected rather than deep-linking the
              // source — the inbox knows how to render every fact kind.
              return (
                <Link
                  key={comm.id}
                  to="/inbox"
                  search={{ item: comm.id }}
                  className={`group/notif hover:bg-muted/50 relative flex w-full items-start px-3 py-2.5 text-left no-underline ${isUnread ? "bg-muted/30" : ""}`}
                  onClick={() => {
                    comm.id && markNotificationRead({ communicationId: comm.id });
                    setOpen(false);
                  }}
                >
                  {isUnread ? (
                    <Button
                      variant="ghost"
                      className="absolute left-0 top-0 h-full w-1 rounded-none border-0 bg-primary p-0 transition-all hover:w-2 hover:bg-primary"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        comm.id && markNotificationRead({ communicationId: comm.id });
                      }}
                    />
                  ) : (
                    <Button
                      variant="ghost"
                      className="absolute left-0 top-0 h-full w-0 rounded-none border-0 bg-muted-foreground/40 p-0 transition-all group-hover/notif:w-1 hover:!w-2 hover:bg-muted-foreground/40"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        comm.id && markNotificationUnread({ communicationId: comm.id });
                      }}
                    />
                  )}
                  <div className="flex-1">
                    <p className="text-muted-foreground text-xs">{comm.sent ? formatRelativeTime(comm.sent) : ""}</p>
                    <p className="text-sm">
                      <span className="inline-flex items-center rounded-full bg-accent px-1.5 py-0.5 text-xs font-medium text-accent-foreground">
                        {senderLabel(comm)}
                      </span>{" "}
                      {kindVerb(comm.kind)}{" "}
                      {/* Only a Task source gets the task phrasing — this used to key
                          off "has a source id", which made an Encounter row read
                          "a task". Anything else falls back to the shared phrase. */}
                      {comm.source?.resourceType === "Task" ? renderTaskPhrase(comm.subjectTask) : subjectPhrase(comm)}
                    </p>
                  </div>
                </Link>
              );
            })
          )}
        </div>
        <div className="border-t">
          <Link
            to="/inbox"
            onClick={() => setOpen(false)}
            className="text-muted-foreground hover:bg-muted/50 block w-full px-3 py-2 text-center text-xs no-underline"
          >
            Open inbox
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
